import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

import { SIGNUP_OR_SIGNIN_POLICY } from '../../constants/user-auth';
import { getLocale } from '../utils';

interface JwtPayload {
  exp: number;
  iat: number;
  email?: string;
  given_name?: string;
  family_name?: string;
  aud: string;
  sub: string;
  acr: string;
  nonce: string;
  auth_time: string;
  globalUid: string;
  phoneNumber: number;
}

export const authenticateWithCode = async (
  authCode: string,
  brandId: string,
) => {
  try {
    const userToken = Cookies.get('user_token');
    let userProfile = null;
    if (userToken) {
      userProfile = await getProfileDetails(userToken, brandId);
      return userProfile;
    } else {
      const token = await getAuthToken(authCode);
      userProfile = await getProfileDetails(token, brandId);
      return userProfile;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Auth error:', error);
  }
};

export const getAuthToken = async (code: string) => {
  const data = await authenticateUser(code);

  if (!data?.id_token) {
    throw new Error('No id_token returned from authenticateUser');
  }

  // Decode JWT token
  const decoded: JwtPayload = jwtDecode(data.id_token);

  // Set cookie expiration based on JWT `exp`
  const expiresInSeconds = decoded.exp - Math.floor(Date.now() / 1000);
  if (expiresInSeconds <= 0) {
    throw new Error('JWT token is expired.');
  }
  const expiresInDays = expiresInSeconds / (60 * 60 * 24);

  // Set JWT token in cookies
  Cookies.set('user_token', data.id_token, {
    expires: expiresInDays,
    secure: true,
    sameSite: 'Strict',
  });

  return data.id_token;
};

export const authenticateUser = async (code: string) => {
  const ICIMAUTHURL = process.env.GATSBY_ICIM_ACCESS_TOKEN_FUNCTION_URL;

  if (!ICIMAUTHURL) {
    // eslint-disable-next-line no-console
    console.error('Acees token Azure function URL missing');
    return;
  }

  const payload = {
    authcode: code,
  };

  try {
    const response = await fetch(ICIMAUTHURL, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'x-functions-key':
          process.env.GATSBY_ICIM_ACCESS_TOKEN_FUNCTION_KEY || '',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Authentication error:', error);
    throw error;
  }
};

export const getProfileDetails = async (authToken: string, brandId: string) => {
  const GET_PROFILE_URL = `${process.env.GATSBY_LIVAT_APP_WRAPPER_FUNCTION_URL}/profile/icim/fetchProfile`;

  // Query params
  const params = new URLSearchParams({
    mpId: brandId,
  });

  const urlWithParams = `${GET_PROFILE_URL}?${params.toString()}`;

  try {
    const response = await fetch(urlWithParams, {
      method: 'GET',
      headers: new Headers({
        'Content-type': 'application/json; charset=utf-8',
        'x-functions-key':
          process.env.GATSBY_LIVAT_APP_WRAPPER_FUNCTION_KEY || '',
        'X-ICIM-JWT': authToken ? authToken : '',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Get User Profile API Error ${response.status}: ${errorText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getProfileDetails Error:', error);
    throw error;
  }
};

// logout user
export const logOutUser = () => {
  // remove auth cookie
  Cookies.remove('user_token');
};

export const createIcimLogoutURL = () => {
  const redirectURL = process.env.GATSBY_ICIM_REGISTERED_REDIRECT_URL || '';
  const encodedRedirectUri = encodeURIComponent(redirectURL);
  const TENANT_ID = process.env.GATSBY_ICIM_TENANT_ID || '';
  const POLICY = SIGNUP_OR_SIGNIN_POLICY;

  const accessToken = Cookies.get('user_token');

  const logoutUrl = `${process.env.GATSBY_ICIM_BASE_URL}/${TENANT_ID}/${POLICY}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodedRedirectUri}&id_token_hint=${accessToken}`;
  return logoutUrl;
};

export const createIcimRedirectURL = (node_locale: string, brandId: string) => {
  const redirectURL = process.env.GATSBY_ICIM_REGISTERED_REDIRECT_URL || '';
  const encodedRedirectUri = encodeURIComponent(redirectURL);
  const CLIENT_ID = process.env.GATSBY_ICIM_CLIENT_ID || '';
  const TENANT_ID = process.env.GATSBY_ICIM_TENANT_ID || '';
  const POLICY = SIGNUP_OR_SIGNIN_POLICY;
  const locale = getLocale(node_locale);

  const authUrl = `${process.env.GATSBY_ICIM_BASE_URL}/${TENANT_ID}/oauth2/v2.0/authorize?p=${POLICY}&client_id=${CLIENT_ID}&nonce=defaultNonce&redirect_uri=${encodedRedirectUri}&scope=openid&response_type=code&brand_id=${brandId}&ui_locales=${locale}`;

  return authUrl;
};
